import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import { initializeAuth, browserLocalPersistence, browserPopupRedirectResolver, connectAuthEmulator } from 'firebase/auth'
import {
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  terminate,
  clearIndexedDbPersistence,
} from 'firebase/firestore'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import { getMessaging, isSupported, type Messaging } from 'firebase/messaging'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
}

const app = initializeApp(firebaseConfig)

// App Check must be initialized before any other Firebase service is used.
// Skipped when VITE_RECAPTCHA_SITE_KEY is absent (e.g. CI, local emulator dev).
if (import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  })
}

// initializeAuth (not getAuth) lets us set localStorage persistence from the very start.
// getAuth() + setPersistence() is async and migrates the session from IndexedDB first —
// when IndexedDB is broken that migration hangs, causing onAuthStateChanged to be slow
// and the skeleton to stay visible for a long time. initializeAuth bypasses that entirely.
export const auth = initializeAuth(app, {
  persistence: [browserLocalPersistence],
  popupRedirectResolver: browserPopupRedirectResolver,
})

// 50 MB cap prevents IndexedDB from growing unbounded, which can corrupt storage
// shared with Firebase Auth (both use IndexedDB in the same origin).
const CACHE_SIZE_BYTES = 50 * 1024 * 1024

function createFirestoreCache() {
  try {
    return persistentLocalCache({ tabManager: persistentMultipleTabManager(), cacheSizeBytes: CACHE_SIZE_BYTES })
  } catch {
    try {
      return persistentLocalCache({ cacheSizeBytes: CACHE_SIZE_BYTES })
    } catch {
      return memoryLocalCache()
    }
  }
}

export const db = initializeFirestore(app, { localCache: createFirestoreCache() })

export const functions = getFunctions(app)

export const storage = getStorage(app)

if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099')
  connectFirestoreEmulator(db, 'localhost', 8080)
  connectFunctionsEmulator(functions, 'localhost', 5001)
  connectStorageEmulator(storage, 'localhost', 9199)
}

// Clears ALL IndexedDB databases for the origin and reloads.
// The backing store error affects all databases simultaneously (App Check, Firestore, Auth),
// so we must delete everything to let Chrome rebuild a clean backing store.
// Returns true if a reload was initiated, false if recovery was already attempted
// (caller should degrade gracefully in that case).
export async function clearFirestoreAndReload(): Promise<boolean> {
  if (sessionStorage.getItem('firestore-idb-recovery')) return false
  sessionStorage.setItem('firestore-idb-recovery', '1')

  try { await terminate(db) } catch { /* already terminated */ }
  try { await clearIndexedDbPersistence(db) } catch { /* ignore — we'll delete manually */ }

  // Delete ALL databases so Chrome can rebuild a clean LevelDB backing store.
  if (indexedDB.databases) {
    const dbs = await indexedDB.databases()
    await Promise.all(
      dbs.map(
        (d) =>
          new Promise<void>((resolve) => {
            if (!d.name) { resolve(); return }
            const req = indexedDB.deleteDatabase(d.name)
            req.onsuccess = () => resolve()
            req.onerror = () => resolve()
          }),
      ),
    )
  }

  window.location.reload()
  return true
}

let messagingInstance: Messaging | null = null

export async function getMessagingInstance(): Promise<Messaging | null> {
  if (messagingInstance) return messagingInstance
  const supported = await isSupported()
  if (!supported) return null
  messagingInstance = getMessaging(app)
  return messagingInstance
}
